import { Badge } from "./ui/badge"
import { cn } from "../lib/utils"

/**
 * StatusBadge - A consistent status badge component
 * Maps semantic status values to ShadCN badge variants
 */
export function StatusBadge({ status, children, className }) {
  // Map status to variant
  const getVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
      case "healthy":
      case "active":
      case "on":
      case "yes":
        return "default" // green primary
      case "cancelled":
      case "failed":
      case "error":
      case "unhealthy":
      case "destructive":
        return "destructive" // red
      case "pending":
      case "warning":
      case "degraded":
      case "stale":
        return "secondary" // neutral/yellow-ish
      case "off":
      case "no":
      case "inactive":
      case "unknown":
      default:
        return "outline" // outlined/muted
    }
  }

  const variant = getVariant(status)

  return (
    <Badge variant={variant} className={cn(className)}>
      {children || status}
    </Badge>
  )
}

/**
 * FreshnessBadge - For database/data freshness indicators
 * @param {number} daysAgo - Number of days since last update
 */
export function FreshnessBadge({ daysAgo, className }) {
  const getStatus = () => {
    if (daysAgo === null || daysAgo === undefined) return "unknown"
    if (daysAgo <= 30) return "success"
    if (daysAgo <= 60) return "warning"
    return "destructive"
  }

  const getLabel = () => {
    if (daysAgo === null || daysAgo === undefined) return "Unknown"
    return `${daysAgo}d ago`
  }

  return (
    <StatusBadge status={getStatus()} className={className}>
      {getLabel()}
    </StatusBadge>
  )
}

/**
 * BooleanBadge - For boolean status indicators (Yes/No, On/Off)
 */
export function BooleanBadge({ value, trueLabel = "Yes", falseLabel = "No", className }) {
  const isTrue = value === true || value === "true" || value === 1 || value === "1"
  const isFalse = value === false || value === "false" || value === 0 || value === "0"

  if (isTrue) {
    return (
      <StatusBadge status="success" className={className}>
        {trueLabel}
      </StatusBadge>
    )
  }

  if (isFalse) {
    return (
      <StatusBadge status="inactive" className={className}>
        {falseLabel}
      </StatusBadge>
    )
  }

  return (
    <StatusBadge status="unknown" className={className}>
      ?
    </StatusBadge>
  )
}
